import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputField from "../../atoms/InputField";
import Button from "../../atoms/Button";
import { toast } from "react-toastify";
import { CREATE_CONTRACT, CREATE_MULTIPLE_PAYMENTS } from "../../../api/apiContratos";
import { PropagateLoader } from "react-spinners";

const validationSchema = Yup.object().shape({
	totalAmount: Yup.number()
		.required("El monto es requerido")
		.positive("El monto debe ser mayor a 0"),
	contractYear: Yup.string()
		.required("El año del contrato es requerido")
		.matches(/^\d{4}$/, "Debe ser un año válido (ej: 2024)"),
	description: Yup.string(),
});

const ModalCrearContrato = ({ clientId, onClose, refetch, userId }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const generateMonthlyPayments = (totalAmount, contractYear) => {
		const paymentAmount = Math.round(totalAmount / 12);
		const payments = [];

		for (let month = 0; month < 12; month++) {
			const paymentDate = new Date(contractYear, month, 1);

			payments.push({
				payment: paymentAmount,
				status: "pendiente",
				date: paymentDate,
				file: null,
			});
		}

		return payments;
	};

	const handleSubmit = async (values) => {
		setIsSubmitting(true);

		try {
			const contractData = {
				totalAmount: values.totalAmount,
				contractYear: values.contractYear,
				userId: userId,
				clientId: parseInt(clientId),
			};

			const newContract = await CREATE_CONTRACT(contractData);

			const payments = generateMonthlyPayments(
				values.totalAmount,
				parseInt(values.contractYear)
			);

			const paymentsWithContractId = payments.map((payment) => ({
				...payment,
				contractId: newContract.id,
			}));

			await CREATE_MULTIPLE_PAYMENTS(paymentsWithContractId);

			toast.success("Contrato creado con éxito. Se generaron 12 pagos mensuales.");
			refetch();
			onClose();
		} catch (error) {
			console.error("Error al crear contrato:", error);
			toast.error("Error al crear el contrato. Intenta de nuevo.");
		} finally {
			setIsSubmitting(false);
		}
	};

	if (isSubmitting) {
		return (
			<div className="w-full h-40 flex justify-center items-center">
				<PropagateLoader color="red" />
			</div>
		);
	}

	return (
		<Formik
			initialValues={{
				totalAmount: "",
				contractYear: new Date().getFullYear().toString(),
				description: "",
			}}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
		>
			{({ values, isValid, dirty }) => (
				<Form className="space-y-6">
					<div className="space-y-4">
						<InputField
							name="totalAmount"
							label="Monto Total Anual a Pagar (MXN)"
							type="number"
							placeholder="Ej: 5000"
							value={values.totalAmount}
						/>
						<InputField
							name="contractYear"
							label="Año del Contrato"
							type="number"
							placeholder="Ej: 2024"
							value={values.contractYear}
						/>
						{values.totalAmount && (
							<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
								<p className="font-semibold text-blue-900">Resumen del Contrato:</p>
								<div className="flex justify-between text-sm">
									<span>Monto anual total:</span>
									<span className="font-semibold">
										${parseInt(values.totalAmount).toLocaleString("es-MX")}
									</span>
								</div>
								<div className="flex justify-between text-sm">
									<span>Monto mensual:</span>
									<span className="font-semibold">
										${(parseInt(values.totalAmount)/ 12).toLocaleString("es-MX")}
									</span>
								</div>
								<div className="flex justify-between text-sm">
									<span>Número de Pagos:</span>
									<span className="font-semibold">12</span>
								</div>
								<div className="flex justify-between text-sm">
									<span>Año:</span>
									<span className="font-semibold">{values.contractYear}</span>
								</div>
								<p className="text-xs text-blue-700 mt-3 italic">
									Se generarán automáticamente 12 pagos mensuales, con fecha el primero de
									cada mes y status "pendiente".
								</p>
							</div>
						)}
					</div>

					<div className="flex gap-4 justify-end pt-4 border-t">
						<Button
							type="button"
							text="Cancelar"
							bgColor="red"
							onClick={onClose}
							disabled={isSubmitting}
						/>
						<Button
							type="submit"
							text="Crear Contrato"
							bgColor="green"
							disabled={!isValid || !dirty || isSubmitting}
						/>
					</div>
				</Form>
			)}
		</Formik>
	);
};

export default ModalCrearContrato;